import React, {
  Fragment,
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import 'react-dates/initialize';
import DayPickerRangeController from 'react-dates/lib/components/DayPickerRangeController';
import isInclusivelyAfterDay from 'react-dates/lib/utils/isInclusivelyAfterDay';
import DatepickerArrow from '../../DatepickerArrow';
import { ListingMapContext } from '../../../context/ListingMapContext';
import useWindowSize from '../../../hooks/useWindowSize';
import WindowQuery from '../../../utils/WindowQuery';
import useOutsideClick from '../../../hooks/useOutsideClick';

export default function DatesDropdown({ visible }) {
  const updateQuery = new WindowQuery();
  const dropdownRef = useRef();

  const {
    setStartDate,
    setEndDate,
    mapStartDate,
    startDate,
    endDate,
    mapEndDate,
    setDropdown,
    datesVisible,
  } = useContext(ListingMapContext);

  const [focusedState, setFocusedState] = useState('startDate');

  const { width: windowWidth } = useWindowSize();

  const [localStartDate, setLocalStartDate] = useState(null);
  const [localEndDate, setLocalEndDate] = useState(null);

  useEffect(() => {
    if (mapStartDate) setLocalStartDate(moment(new Date(mapStartDate)));
    if (mapEndDate) setLocalEndDate(moment(new Date(mapEndDate)));
  }, [mapEndDate, mapStartDate]);

  useOutsideClick(dropdownRef, datesVisible, () => setDropdown(null));

  // Prevents date picker from rendering before container width is set
  if (!visible) {
    return null;
  }

  return (
    // className important for custom styling
    <div ref={dropdownRef} className="custom-calendar-wrapper">
      {/* Only show on mobile screens */}
      {windowWidth <= 768 && (
        <Fragment>
          <h2 className="dropdown-title">Select Dates</h2>
          <button
            aria-label="Close Dates Dropdown"
            name="Close Dates Dropdown"
            type="button"
            className="mobile-dropdown-cancel btn btn--small"
            onClick={() => {
              setLocalStartDate(startDate);
              setLocalEndDate(endDate);
              setDropdown(null);
            }}
          >
            Close
          </button>
        </Fragment>
      )}
      <DayPickerRangeController
        readOnly
        hideKeyboardShortcutsPanel
        startDate={localStartDate}
        endDate={localEndDate}
        onDatesChange={({ startDate: newStartDate, endDate: newEndDate }) => {
          setLocalStartDate(newStartDate);
          setLocalEndDate(newEndDate);
        }}
        focusedInput={focusedState || 'startDate'}
        onFocusChange={e => setFocusedState(e)}
        numberOfMonths={windowWidth <= 630 ? 1 : 2}
        noBorder
        navNext={<DatepickerArrow direction="next" />}
        navPrev={<DatepickerArrow direction="prev" />}
        isOutsideRange={day => !isInclusivelyAfterDay(day, moment())}
      />
      <section
        className="dropdown-apply"
        style={{ justifyContent: 'flex-end' }}
      >
        <button
          aria-label="Apply Dates Filter"
          name="Apply Dates Filter"
          type="button"
          className="btn btn--dark btn--small apply"
          onClick={() => {
            if (localStartDate) {
              updateQuery.add(
                'check_in',
                moment(localStartDate).format('YYYYMMDD')
              );
            } else updateQuery.remove('check_in');

            if (localEndDate) {
              updateQuery.add(
                'check_out',
                moment(localEndDate).format('YYYYMMDD')
              );
            } else updateQuery.remove('check_out');

            setStartDate(localStartDate);
            setEndDate(localEndDate);
            setDropdown(null);
          }}
        >
          Apply
        </button>
        <button
          aria-label="Clear Dates Filter"
          name="Clear Dates Filter"
          type="button"
          className="btn btn--small clear-filter"
          onClick={() => {
            setStartDate(null);
            setEndDate(null);
            setLocalStartDate(null);
            setLocalEndDate(null);
            updateQuery.remove('check_in');
            updateQuery.remove('check_out');
          }}
        >
          Clear
        </button>
      </section>
    </div>
  );
}

DatesDropdown.propTypes = {
  visible: PropTypes.bool.isRequired, // !!! IMPORTANT !!!
};
